/**
 * MentionsList — every place this person was named, newest first (2026-09-17).
 *
 * The catch-up view. A row says who named you, in which thread, when, and what
 * they said; pressing it opens that thread and lands on that message through the
 * same scroll-and-glow a search hit rides. Around each one it shows the message
 * before and the message after, so the point of the mention is legible without
 * opening anything — which is what Miles asked for ("a message before, a message
 * after… so they kinda understand it, caught up quickly, all in one view").
 *
 * THE CONTEXT IS READ LIVE, IN ONE QUERY PER THREAD. It cannot be stored on the
 * row: at the instant a mention is filed the messages after it do not exist yet,
 * and a stored "before" goes stale. The host plans the windows
 * (`planContextWindows`) and hands this component whatever came back, so a
 * thirty-row list costs a handful of reads rather than thirty — and a window that
 * fails degrades to the row's own stored snippet, never to a spinner that never
 * resolves.
 *
 * PEOPLE FIRST. The default filter hides Prest0n's own mentions: he addresses the
 * user in nearly every reply, so an unfiltered list is almost entirely his and
 * buries exactly what this exists to surface. `all` is one press away.
 *
 * Copy is chrome in the viewer's language (D9) through `threadListCopy`. The
 * snippets are other people's words, verbatim, rendered as TEXT — never as html.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { HoverCardPortal } from './HoverCardPortal';
import { threadListCopy } from './threadListCopy';
import { BODY_PORTAL_Z_INDEX } from '../../styles/layers';
import { isCoarsePointer } from './composerEnterPolicy';
import type { PickerAnchor, PickerRect } from '../../services/messageReactions/pickerPlacement';
import type { ThreadClosure } from '../../services/groupThreads/threadClosure';
import {
  threadClosurePill,
  threadClosureRowName,
  threadClosureSentence,
  threadClosureWhyLabel,
} from '../../services/groupThreads/threadClosureCopy';
import type { ThreadListLang } from '../../types/threadCategories';
import type { MentionReadState, ThreadMention } from '../../types/threadMention';
import { isMentionUnread } from '../../types/threadMention';
import type { MentionContextMessage, MentionFilter } from '../../services/threadMentions/mentionInboxModel';

export interface MentionRowContext {
  before: MentionContextMessage[];
  after: MentionContextMessage[];
}

export interface MentionsListProps {
  rows: readonly ThreadMention[];
  readState: MentionReadState;
  filter: MentionFilter;
  onFilterChange: (filter: MentionFilter) => void;
  /** Open the thread and land on the message. */
  onOpen: (row: ThreadMention) => void;
  onMarkAllRead: () => void;
  /** Context by anchor id — absent while a window is still in flight, or after it failed. */
  contextByMentionId?: Readonly<Record<string, MentionRowContext>>;
  /** Which rows are expanded to the wider radius. */
  expandedIds?: ReadonlySet<string>;
  onToggleExpanded?: (id: string) => void;
  /** Relative time, the rail's own formatter — passed in so this file holds no clock. */
  formatWhen: (orderTs: number) => string;
  lang?: ThreadListLang;
  /**
   * Threads closed to the viewer. THE SAME SET the tab's badge counts against,
   * handed down rather than recomputed: the badge drops retired rows, so the
   * unread EMPHASIS here has to drop the same ones or the tab reads 0 above
   * three gold-bordered rows with a "mark all read" offering to clear nothing.
   */
  retiredThreadIds?: ReadonlySet<string>;
  /** The resolved verdict per retired row, for the words. */
  closureByMentionId?: Readonly<Record<string, ThreadClosure>>;
  /**
   * The chat pane's rect, so the hover card clamps inside it. Omitting it is how
   * a card straddles the docked pane's edge — the defect placeReactionPicker's
   * bounds argument exists for.
   */
  paneBounds?: () => PickerRect | null;
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  /* The rail's own chrome inset, so this row starts on the same vertical
     line as the tab strip, the search box and the workspace card above it.
     The fallback is the sheet's spacing, which renders this component too
     (see ThreadSidebarContainer's variable contract). */
  gap: 8px;
  padding: 0 var(--rail-chrome-px, 12px) var(--rail-chrome-pb, 6px);
`;

/* ONE SEGMENTED-CONTROL LANGUAGE ACROSS THE TOP SECTION (2026-09-21). The
   scope toggle in the workspace card, the tab strip and this filter were
   three different shapes — a pill, a rounded track and a 7px-radius box —
   stacked within ninety vertical pixels of each other. They are one shape
   now: a fully rounded track, a gold-filled active segment with near-black
   text, and legible light text on the rest. Metrics ride the same
   --rail-segment-* variables, so the phone sheet (outside the rail, hence
   the fallbacks) keeps its own comfortable sizing. */
const FilterGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border: 1px solid rgba(198, 167, 94, 0.32);
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.22);
`;

const FilterButton = styled.button<{ $active: boolean }>`
  padding: var(--rail-segment-py, 3px) var(--rail-segment-px, 10px);
  border: none;
  border-radius: 999px;
  background: ${(p) => (p.$active ? 'var(--brand-gold)' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--brand-black)' : 'rgba(250, 250, 248, 0.82)')};
  box-shadow: ${(p) => (p.$active ? '0 1px 6px rgba(0, 0, 0, 0.38)' : 'none')};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: var(--rail-segment-size, 0.7rem);
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  letter-spacing: 0.02em;
  line-height: 1.4;
  white-space: nowrap;
  cursor: ${(p) => (p.$active ? 'default' : 'pointer')};
  transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;

  &:hover { background: ${(p) => (p.$active ? 'var(--brand-gold)' : 'rgba(198, 167, 94, 0.16)')}; }
  &:focus-visible { outline: 2px solid rgba(198, 167, 94, 0.8); outline-offset: -2px; }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** The quiet lowercase ghost button the rail's section headers already use. */
const GhostAction = styled.button`
  margin-left: auto;
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 3px 9px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: transparent;
  color: rgba(182, 146, 76, 0.9);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: var(--rail-segment-size, 0.68rem);
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;

  &:hover {
    background: rgba(198, 167, 94, 0.12);
    border-color: rgba(198, 167, 94, 0.28);
    color: var(--brand-gold);
  }
  &:focus-visible { outline: 2px solid rgba(198, 167, 94, 0.8); outline-offset: 1px; }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const List = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0 8px 12px;
  scrollbar-width: thin;
  scrollbar-color: rgba(198, 167, 94, 0.25) transparent;
`;

const Row = styled.div<{ $unread: boolean; $retired?: boolean }>`
  margin: 0 0 6px;
  /* THE DIM IS opacity, NEVER A COLOUR. Three gates make that the only safe
     expression: check-design-tokens reads a hardcoded brand channel triple
     carrying an INTERPOLATED alpha as a palette value escaping theme.ts (the
     interpolation does not satisfy its alpha capture, so it falls through to the
     brand-RGB map and reports an offender — which is also why this comment says
     that in words: inside a styled template literal a dollar-brace is a real
     interpolation and a backtick CLOSES the literal, CSS comment or not, so a
     comment here may not quote either shape); check-gold-as-text would demand
     a new gold "color:" declaration, and gold-text-baseline.json keys this file's rows by
     ORDINAL, so inserting one renumbers the rest. opacity has zero palette
     surface and greys the border, the left rail and the snippet at once, which
     is what "greyed out" means. It lifts on hover/focus so the summary the
     owner asked to keep readable is easiest to read exactly when being read. */
  opacity: ${(p) => (p.$retired ? 0.68 : 1)};
  transition: opacity 0.16s ease;
  &:hover, &:focus-within { opacity: ${(p) => (p.$retired ? 0.94 : 1)}; }
  @media (prefers-reduced-motion: reduce) { transition: none; }
  /* The whole colour rides the interpolation, never a hardcoded channel triple
     with an interpolated alpha — the design-token gate reads the latter as a
     palette value escaping src/styles/theme.ts. */
  border: 1px solid ${(p) => (p.$unread ? 'rgba(198, 167, 94, 0.3)' : 'rgba(198, 167, 94, 0.14)')};
  border-left: 3px solid ${(p) => (p.$unread ? 'var(--brand-gold)' : 'rgba(198, 167, 94, 0.2)')};
  border-radius: 8px;
  background: ${(p) => (p.$unread ? 'rgba(198, 167, 94, 0.08)' : 'rgba(198, 167, 94, 0.03)')};
`;

const RowButton = styled.button`
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 7px 10px 6px;
  border: none;
  border-radius: 8px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  &:hover { background: rgba(198, 167, 94, 0.07); }
  &:focus-visible { outline: 2px solid rgba(198, 167, 94, 0.6); outline-offset: -2px; }
`;

/** The retired row's button: focusable, announced, and inert. */
const RetiredRowButton = styled(RowButton)`
  cursor: not-allowed;
  &:hover { background: transparent; }
`;

/**
 * The "why" control — a REAL button, and it must render AFTER RowButton.
 *
 * The live prod-e2e row presses `[data-mention-row] button` through a helper
 * that clicks the FIRST usable match and whose only exclusion is `el.disabled`
 * (thread-experience.prod.spec.ts). `aria-disabled` does not satisfy that, so a
 * control rendered ahead of the row button would silently become the click
 * target and the suite's "landed on the message" assertion would fail dozens of
 * lines later. Neutral rather than gold: in this palette gold reads as
 * actionable, and this is the one control on the row that still is.
 */
const ClosedWhyButton = styled.button`
  display: block;
  width: 100%;
  padding: 0 10px 6px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 10.5px;
  text-align: left;
  cursor: pointer;

  &:hover { color: rgba(255, 255, 255, 0.78); }
  &:focus-visible { outline: 2px solid rgba(198, 167, 94, 0.6); outline-offset: -2px; }
`;

/**
 * The explanation, IN FLOW. Not absolute: a positioned tooltip inside this
 * list's `overflow-y: auto` container is clipped into invisibility by
 * construction (the ThreadChipTooltip lesson). This is the coarse-pointer
 * channel, where there is no hover to reveal anything.
 */
const ClosedReason = styled.div`
  padding: 0 10px 7px;
  color: rgba(255, 255, 255, 0.62);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11px;
  line-height: 1.35;
`;

const RowHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 3px;
  color: var(--brand-gold);
  font-size: 11.5px;
  font-weight: 600;
`;

const RowWhere = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.5);
  font-size: 10.5px;
  font-weight: 500;
`;

const RowWhen = styled.span`
  flex: 0 0 auto;
  color: rgba(255, 255, 255, 0.4);
  font-size: 10.5px;
  font-weight: 500;
`;

const Snippet = styled.div`
  color: rgba(255, 255, 255, 0.88);
  font-size: 12px;
  line-height: 1.4;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const ContextLine = styled.div`
  padding: 2px 10px;
  color: rgba(255, 255, 255, 0.45);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11px;
  line-height: 1.35;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ContextWho = styled.span`
  color: rgba(255, 255, 255, 0.62);
  font-weight: 600;
`;

const ExpandButton = styled.button`
  display: block;
  width: 100%;
  padding: 3px 10px 6px;
  border: none;
  background: transparent;
  color: rgba(182, 146, 76, 0.8);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 10.5px;
  text-align: left;
  cursor: pointer;

  &:hover { color: var(--brand-gold); }
  &:focus-visible { outline: 2px solid rgba(198, 167, 94, 0.6); outline-offset: -2px; }
`;

const Empty = styled.div`
  padding: 18px 14px;
  color: rgba(255, 255, 255, 0.45);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11.5px;
  text-align: center;
  line-height: 1.5;
`;

function contextLine(m: MentionContextMessage, key: string): React.ReactElement {
  return (
    <ContextLine key={key} data-mention-context={key}>
      {m.senderName ? <ContextWho>{m.senderName}: </ContextWho> : null}
      {m.text}
    </ContextLine>
  );
}

export const MentionsList: React.FC<MentionsListProps> = ({
  rows,
  readState,
  filter,
  onFilterChange,
  onOpen,
  onMarkAllRead,
  contextByMentionId,
  expandedIds,
  onToggleExpanded,
  formatWhen,
  lang = 'en',
  retiredThreadIds,
  closureByMentionId,
  paneBounds,
}) => {
  const copy = threadListCopy(lang);
  const retired = retiredThreadIds;
  const isRetired = useCallback(
    (threadId: string): boolean => Boolean(retired && retired.size > 0 && retired.has(threadId)),
    [retired],
  );
  // The mark-all-read control is gated on the SAME predicate the tab's badge
  // counts with — the second of the four unread readings in this file, and the
  // one that would otherwise offer to clear a count of zero.
  const anyUnread = rows.some((row) => !isRetired(row.threadId) && isMentionUnread(row, readState));

  // Fine pointers get the portaled card; coarse pointers have no hover at all,
  // so they get the in-flow reveal below and nothing is measured for them.
  // Read ONCE: the primary pointer does not change under a mounted list, and a
  // matchMedia call on every render of a fifty-row list is pure waste.
  const coarseRef = useRef<boolean | null>(null);
  if (coarseRef.current === null) coarseRef.current = isCoarsePointer();
  const [hovered, setHovered] = useState<{ id: string; anchor: PickerAnchor } | null>(null);
  const [revealedId, setRevealedId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const revealFrom = useCallback((id: string, el: HTMLElement | null): void => {
    if (coarseRef.current || !el) return;
    const r = el.getBoundingClientRect();
    setHovered({ id, anchor: { x: r.left + r.width / 2, y: r.top, width: 0, height: 0 } });
  }, []);

  // THE CARD CLOSES ON SCROLL. Mouse-hover-then-wheel happens to survive (the
  // row slides from under a stationary pointer and fires pointerleave), but
  // KEYBOARD focus plus a wheel scroll fires neither blur nor pointerleave — so
  // without this the card stays pinned to stale viewport coordinates, hovering
  // over a different row entirely.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return undefined;
    const close = (): void => setHovered(null);
    el.addEventListener('scroll', close, { passive: true });
    window.addEventListener('resize', close);
    return () => {
      el.removeEventListener('scroll', close);
      window.removeEventListener('resize', close);
    };
  }, []);

  return (
    <Wrap data-mentions-list="">
      <HeaderRow>
        <FilterGroup role="group" aria-label={copy.mentionsFilterLabel}>
          <FilterButton
            type="button"
            data-mentions-filter="people"
            $active={filter === 'people'}
            aria-pressed={filter === 'people'}
            onClick={() => onFilterChange('people')}
          >
            {copy.mentionsFromPeople}
          </FilterButton>
          <FilterButton
            type="button"
            data-mentions-filter="all"
            $active={filter === 'all'}
            aria-pressed={filter === 'all'}
            onClick={() => onFilterChange('all')}
          >
            {copy.mentionsFromAll}
          </FilterButton>
        </FilterGroup>
        {anyUnread && (
          <GhostAction type="button" data-mentions-mark-all="" onClick={onMarkAllRead}>
            {copy.markAllRead}
          </GhostAction>
        )}
      </HeaderRow>

      <List ref={listRef}>
        {rows.length === 0 && (
          <Empty data-mentions-empty="">{copy.noMentionsYet}</Empty>
        )}
        {rows.map((row) => {
          const unread = isMentionUnread(row, readState);
          const who = row.byPrest0n ? copy.prest0n : (row.senderName || copy.prest0n);
          const headline = row.reason === 'reply' ? copy.repliedToYou(who) : copy.mentionedYou(who);
          const ctx = contextByMentionId?.[row.id];
          const expanded = expandedIds?.has(row.id) === true;
          const hasContext = Boolean(ctx && (ctx.before.length > 0 || ctx.after.length > 0));
          // A thread closed to the viewer. The row STAYS — greyed, legible, and
          // able to say why (owner directive 2026-09-21) — and opens nothing.
          const closure = closureByMentionId?.[row.id] ?? null;
          const rowRetired = closure !== null || isRetired(row.threadId);
          const reason = closure ? threadClosureSentence(closure, lang) : '';
          const RowPress = rowRetired ? RetiredRowButton : RowButton;
          return (
            <Row
              key={row.id}
              // The unread EMPHASIS drops with the badge; the ATTRIBUTE below
              // keeps reporting the row's true read state, because that is a
              // fact about the person's inbox and the live prod-e2e row reads it.
              $unread={unread && !rowRetired}
              $retired={rowRetired}
              data-mention-row={row.id}
              data-mention-unread={unread ? 'true' : 'false'}
              data-mention-reason={row.reason}
              // Present-or-absent, never "false": a live row's markup stays
              // byte-identical to what shipped before this change.
              {...(rowRetired ? { 'data-mention-retired': 'true' } : {})}
            >
              {ctx?.before.map((m, i) => contextLine(m, `before-${row.id}-${m.id || i}`))}
              <RowPress
                type="button"
                // The retired fact leads the NAME, not a description. A native
                // title or aria-describedby is announced after the name and is
                // routinely suppressed by verbosity settings — and a native
                // title beside the portaled card stacks TWO tooltips, which
                // this tree already tried and reversed (ThreadSwitcherBar).
                aria-label={closure
                  ? threadClosureRowName(closure, `${headline} · ${row.threadTitle}`, lang)
                  : copy.openMention(who)}
                // aria-disabled, never disabled: a disabled button fires no
                // pointer events and takes no focus, and the explanation is
                // reached by hovering or focusing this very control.
                aria-disabled={rowRetired ? true : undefined}
                onMouseEnter={rowRetired ? (e) => revealFrom(row.id, e.currentTarget) : undefined}
                onFocus={rowRetired ? (e) => revealFrom(row.id, e.currentTarget) : undefined}
                onMouseLeave={rowRetired ? () => setHovered(null) : undefined}
                onBlur={rowRetired ? () => setHovered(null) : undefined}
                onClick={rowRetired
                  ? (e) => {
                    // aria-disabled does not stop a click — the controller
                    // refuses too, and this is the belt in front of it.
                    e.preventDefault();
                    if (coarseRef.current) setRevealedId((prev) => (prev === row.id ? null : row.id));
                  }
                  : () => onOpen(row)}
              >
                <RowHead>
                  <span>{headline}</span>
                  <RowWhere>{row.threadTitle}</RowWhere>
                  <RowWhen>{formatWhen(row.orderTs)}</RowWhen>
                </RowHead>
                <Snippet>{row.snippet}</Snippet>
              </RowPress>
              {ctx?.after.map((m, i) => contextLine(m, `after-${row.id}-${m.id || i}`))}
              {closure && (
                <ClosedWhyButton
                  type="button"
                  data-mention-closed-why={row.id}
                  /* WCAG 2.5.3 Label in Name. The visible content of this
                     control is the pill word ("archived"), so the accessible
                     name has to CONTAIN it or voice control cannot address the
                     button a sighted user is looking at — "click archived"
                     matches nothing. It shipped as the why-label alone, which
                     has zero overlap with the visible text; leading with the
                     pill fixes that and keeps the retired fact first, which is
                     what threadClosureRowName exists for. */
                  aria-label={threadClosureRowName(closure, threadClosureWhyLabel(lang), lang)}
                  aria-expanded={revealedId === row.id}
                  onClick={() => setRevealedId((prev) => (prev === row.id ? null : row.id))}
                >
                  {threadClosurePill(closure, lang)}
                </ClosedWhyButton>
              )}
              {closure && revealedId === row.id && (
                <ClosedReason data-mention-closed-reason={row.id}>{reason}</ClosedReason>
              )}
              {closure && !coarseRef.current && hovered?.id === row.id && (
                <HoverCardPortal
                  anchor={hovered.anchor}
                  bounds={paneBounds?.() ?? null}
                  zIndex={BODY_PORTAL_Z_INDEX}
                  markers={{ 'data-mention-closed-card': row.id }}
                  placeKey={reason}
                >
                  {reason}
                </HoverCardPortal>
              )}
              {/* No expand affordance on a retired row: its thread issues no new
                  context read, and the stored snippet above is the summary. */}
              {onToggleExpanded && hasContext && !rowRetired && (
                <ExpandButton
                  type="button"
                  data-mention-expand={row.id}
                  aria-expanded={expanded}
                  onClick={() => onToggleExpanded(row.id)}
                >
                  {expanded ? copy.hideContext : copy.showContext}
                </ExpandButton>
              )}
            </Row>
          );
        })}
      </List>
    </Wrap>
  );
};

export default MentionsList;
